import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  BookOpen,
  TrendingUp,
  Calendar,
  Award,
  Target,
  Clock,
  CheckCircle,
  AlertTriangle,
  Plus,
  Edit3,
  BarChart3,
  Star,
  GraduationCap,
  Users
} from 'lucide-react';
import { Card } from '../components/shared/Card';
import { Button } from '../components/shared/Button';
import { Badge } from '../components/shared/Badge';
import { ProgressBar } from '../components/shared/ProgressBar';

interface Course {
  id: string;
  name: string;
  code: string;
  credits: number;
  grade: string;
  gpa: number;
  semester: string;
  year: string;
  type: 'core' | 'ap' | 'honors' | 'elective';
  teacher: string;
  completed: boolean;
}

interface AcademicGoal {
  id: string;
  title: string;
  description: string;
  category: 'gpa' | 'courses' | 'testing' | 'skills';
  targetDate: string;
  progress: number;
  status: 'active' | 'completed' | 'at-risk';
}

const currentCourses: Course[] = [
  {
    id: '1',
    name: 'AP Chemistry',
    code: 'CHEM301',
    credits: 1.0,
    grade: 'A-',
    gpa: 4.7,
    semester: 'Fall',
    year: '2024',
    type: 'ap',
    teacher: 'Ms. Johnson',
    completed: false
  },
  {
    id: '2',
    name: 'Honors English Literature',
    code: 'ENG201',
    credits: 1.0,
    grade: 'A',
    gpa: 4.3,
    semester: 'Fall',
    year: '2024',
    type: 'honors',
    teacher: 'Mr. Davis',
    completed: false
  },
  {
    id: '3',
    name: 'AP Calculus BC',
    code: 'MATH401',
    credits: 1.0,
    grade: 'A+',
    gpa: 5.0,
    semester: 'Fall',
    year: '2024',
    type: 'ap',
    teacher: 'Dr. Wilson',
    completed: false
  },
  {
    id: '4',
    name: 'World History',
    code: 'HIST101',
    credits: 1.0,
    grade: 'B+',
    gpa: 3.7,
    semester: 'Fall',
    year: '2024',
    type: 'core',
    teacher: 'Ms. Rodriguez',
    completed: false
  },
  {
    id: '5',
    name: 'Physics',
    code: 'PHYS201',
    credits: 1.0,
    grade: 'A-',
    gpa: 4.0,
    semester: 'Fall',
    year: '2024',
    type: 'core',
    teacher: 'Mr. Thompson',
    completed: false
  }
];

const academicGoals: AcademicGoal[] = [
  {
    id: '1',
    title: 'Maintain 3.8+ GPA',
    description: 'Keep cumulative GPA above 3.8 to stay competitive for top universities',
    category: 'gpa',
    targetDate: '2025-06-15',
    progress: 92,
    status: 'active'
  },
  {
    id: '2',
    title: 'Complete 8 AP Courses',
    description: 'Take 8 AP courses by graduation to demonstrate academic rigor',
    category: 'courses',
    targetDate: '2025-06-15',
    progress: 37,
    status: 'active'
  },
  {
    id: '3',
    title: 'SAT Score 1500+',
    description: 'Achieve SAT score of 1500 or higher for competitive college admissions',
    category: 'testing',
    targetDate: '2025-03-01',
    progress: 75,
    status: 'active'
  },
  {
    id: '4',
    title: 'National Honor Society',
    description: 'Qualify for and join National Honor Society',
    category: 'skills',
    targetDate: '2024-12-01',
    progress: 88,
    status: 'active'
  }
];

export const Academics: React.FC = () => {
  const [selectedView, setSelectedView] = useState<'overview' | 'courses' | 'goals' | 'planning'>('overview');

  const calculateGPA = () => {
    const totalPoints = currentCourses.reduce((sum, course) => sum + (course.gpa * course.credits), 0);
    const totalCredits = currentCourses.reduce((sum, course) => sum + course.credits, 0);
    return (totalPoints / totalCredits).toFixed(2);
  };

  const getGradeColor = (grade: string) => {
    if (grade.startsWith('A')) return 'text-green-600';
    if (grade.startsWith('B')) return 'text-blue-600';
    if (grade.startsWith('C')) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getCourseTypeColor = (type: string) => {
    switch (type) {
      case 'ap': return 'bg-red-100 text-red-800';
      case 'honors': return 'bg-blue-100 text-blue-800';
      case 'core': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'text-green-600';
      case 'active': return 'text-blue-600';
      case 'at-risk': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <Card className="bg-gradient-to-br from-primary-500 to-accent-500 text-white">
        <div className="text-center">
          <BookOpen className="w-12 h-12 mx-auto mb-4" />
          <h2 className="text-3xl font-bold mb-4">Academic Dashboard</h2>
          <p className="text-primary-100 text-lg mb-6">
            Track your academic progress, manage coursework, and plan for college success
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white bg-opacity-20 rounded-xl p-4">
              <div className="text-2xl font-bold">{calculateGPA()}</div>
              <div className="text-sm text-primary-100">Current GPA</div>
            </div>
            <div className="bg-white bg-opacity-20 rounded-xl p-4">
              <div className="text-2xl font-bold">{currentCourses.filter(c => c.type === 'ap').length}</div>
              <div className="text-sm text-primary-100">AP Courses</div>
            </div>
            <div className="bg-white bg-opacity-20 rounded-xl p-4">
              <div className="text-2xl font-bold">{academicGoals.filter(g => g.status === 'active').length}</div>
              <div className="text-sm text-primary-100">Active Goals</div>
            </div>
          </div>
        </div>
      </Card>

      {/* Navigation */}
      <Card>
        <div className="flex border-b border-gray-200">
          {[
            { id: 'overview', label: 'Overview', icon: BarChart3 },
            { id: 'courses', label: 'Current Courses', icon: BookOpen },
            { id: 'goals', label: 'Academic Goals', icon: Target },
            { id: 'planning', label: 'Course Planning', icon: Calendar }
          ].map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setSelectedView(id as any)}
              className={`flex items-center space-x-2 px-6 py-4 font-medium text-sm border-b-2 transition-colors ${
                selectedView === id
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Icon size={16} />
              <span>{label}</span>
            </button>
          ))}
        </div>
      </Card>

      {/* Content */}
      {selectedView === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* GPA Trend */}
          <Card>
            <h3 className="text-lg font-semibold text-gray-900 mb-6">GPA Trend</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Cumulative GPA</span>
                <span className="text-2xl font-bold text-green-600">{calculateGPA()}</span>
              </div>
              <ProgressBar progress={parseFloat(calculateGPA()) / 4 * 100} color="primary" showPercentage={false} />

              <div className="grid grid-cols-2 gap-4 mt-6">
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <div className="text-xl font-bold text-blue-600">3.9</div>
                  <div className="text-xs text-gray-600">Target GPA</div>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <div className="text-xl font-bold text-green-600">4.2</div>
                  <div className="text-xs text-gray-600">Weighted GPA</div>
                </div>
              </div>
            </div>
          </Card>

          {/* Course Distribution */}
          <Card>
            <h3 className="text-lg font-semibold text-gray-900 mb-6">Course Distribution</h3>
            <div className="space-y-4">
              {[
                { type: 'AP Courses', count: currentCourses.filter(c => c.type === 'ap').length, color: 'red', total: 3 },
                { type: 'Honors Courses', count: currentCourses.filter(c => c.type === 'honors').length, color: 'blue', total: 2 },
                { type: 'Core Courses', count: currentCourses.filter(c => c.type === 'core').length, color: 'green', total: 4 }
              ].map((item, index) => (
                <div key={index}>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-gray-700">{item.type}</span>
                    <span className="text-sm font-semibold text-gray-900">{item.count} courses</span>
                  </div>
                  <ProgressBar progress={(item.count / item.total) * 100} color={item.color as any} size="sm" />
                </div>
              ))}
            </div>
          </Card>

          {/* Recent Achievements */}
          <Card>
            <h3 className="text-lg font-semibold text-gray-900 mb-6">Recent Achievements</h3>
            <div className="space-y-3">
              <div className="flex items-center space-x-3 p-3 bg-green-50 rounded-lg">
                <Award className="text-green-600" size={20} />
                <div>
                  <div className="font-semibold text-gray-900">Perfect Score on Chemistry Quiz</div>
                  <div className="text-sm text-gray-600">October 15, 2024</div>
                </div>
              </div>
              <div className="flex items-center space-x-3 p-3 bg-blue-50 rounded-lg">
                <Star className="text-blue-600" size={20} />
                <div>
                  <div className="font-semibold text-gray-900">Made Honor Roll</div>
                  <div className="text-sm text-gray-600">September 2024</div>
                </div>
              </div>
              <div className="flex items-center space-x-3 p-3 bg-purple-50 rounded-lg">
                <GraduationCap className="text-purple-600" size={20} />
                <div>
                  <div className="font-semibold text-gray-900">Completed Summer Math Program</div>
                  <div className="text-sm text-gray-600">August 2024</div>
                </div>
              </div>
            </div>
          </Card>

          {/* Academic Goals Progress */}
          <Card>
            <h3 className="text-lg font-semibold text-gray-900 mb-6">Goals Progress</h3>
            <div className="space-y-4">
              {academicGoals.slice(0, 3).map((goal) => (
                <div key={goal.id}>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-gray-700">{goal.title}</span>
                    <span className="text-sm font-semibold text-gray-900">{goal.progress}%</span>
                  </div>
                  <ProgressBar progress={goal.progress} color="primary" size="sm" />
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}

      {selectedView === 'courses' && (
        <div className="space-y-6">
          <Card>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Current Courses - Fall 2024</h3>
              <Button size="sm">
                <Plus size={16} className="mr-2" />
                Add Course
              </Button>
            </div>

            <div className="space-y-4">
              {currentCourses.map((course, index) => (
                <motion.div
                  key={course.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="border border-gray-200 rounded-xl p-6"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h4 className="text-lg font-semibold text-gray-900">{course.name}</h4>
                        <Badge className={getCourseTypeColor(course.type)} size="sm">
                          {course.type.toUpperCase()}
                        </Badge>
                        <span className="text-sm text-gray-500">({course.code})</span>
                      </div>
                      <div className="flex items-center space-x-4 text-sm text-gray-600 mb-3">
                        <span>Teacher: {course.teacher}</span>
                        <span>•</span>
                        <span>{course.credits} credit</span>
                        <span>•</span>
                        <span>{course.semester} {course.year}</span>
                      </div>
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center space-x-2">
                          <span className="text-sm text-gray-600">Current Grade:</span>
                          <span className={`text-lg font-bold ${getGradeColor(course.grade)}`}>
                            {course.grade}
                          </span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="text-sm text-gray-600">GPA Impact:</span>
                          <span className="text-sm font-semibold text-primary-600">
                            {course.gpa.toFixed(1)}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <Button variant="ghost" size="sm">
                        <Edit3 size={14} />
                      </Button>
                      <Button variant="outline" size="sm">
                        View Details
                      </Button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </Card>
        </div>
      )}

      {selectedView === 'goals' && (
        <div className="space-y-6">
          <Card>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Academic Goals</h3>
              <Button size="sm">
                <Plus size={16} className="mr-2" />
                New Goal
              </Button>
            </div>

            <div className="space-y-4">
              {academicGoals.map((goal, index) => (
                <motion.div
                  key={goal.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="border border-gray-200 rounded-xl p-6"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h4 className="text-lg font-semibold text-gray-900">{goal.title}</h4>
                        <span className={`text-sm font-medium ${getStatusColor(goal.status)}`}>
                          {goal.status.replace('-', ' ')}
                        </span>
                      </div>
                      <p className="text-gray-600 mb-3">{goal.description}</p>
                      <div className="flex items-center space-x-4 text-sm text-gray-500">
                        <div className="flex items-center space-x-1">
                          <Calendar size={14} />
                          <span>Due: {new Date(goal.targetDate).toLocaleDateString()}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Target size={14} />
                          <span>{goal.category}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mb-4">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium text-gray-700">Progress</span>
                      <span className="text-sm font-semibold text-gray-900">{goal.progress}%</span>
                    </div>
                    <ProgressBar progress={goal.progress} color="primary" size="sm" />
                  </div>

                  <div className="flex items-center space-x-2">
                    <Button variant="outline" size="sm">
                      <Edit3 size={14} className="mr-2" />
                      Edit Goal
                    </Button>
                    {goal.progress < 100 && (
                      <Button size="sm">
                        <CheckCircle size={14} className="mr-2" />
                        Mark Complete
                      </Button>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          </Card>
        </div>
      )}

      {selectedView === 'planning' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <h3 className="text-lg font-semibold text-gray-900 mb-6">Graduation Requirements</h3>
            <div className="space-y-4">
              {[
                { subject: 'English', required: 4, completed: 2, planned: 1 },
                { subject: 'Mathematics', required: 4, completed: 3, planned: 1 },
                { subject: 'Science', required: 3, completed: 2, planned: 2 },
                { subject: 'Social Studies', required: 3, completed: 2, planned: 1 },
                { subject: 'Foreign Language', required: 2, completed: 1, planned: 1 },
                { subject: 'Electives', required: 6, completed: 3, planned: 2 }
              ].map((req, index) => {
                const total = req.completed + req.planned;
                const isOnTrack = total >= req.required;

                return (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-gray-900">{req.subject}</span>
                        <div className="flex items-center space-x-2">
                          {isOnTrack ? (
                            <CheckCircle className="text-green-500" size={16} />
                          ) : (
                            <AlertTriangle className="text-yellow-500" size={16} />
                          )}
                          <span className="text-sm font-semibold">
                            {total}/{req.required}
                          </span>
                        </div>
                      </div>
                      <ProgressBar
                        progress={(total / req.required) * 100}
                        color={isOnTrack ? 'primary' : 'accent'}
                        size="sm"
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>

          <Card>
            <h3 className="text-lg font-semibold text-gray-900 mb-6">Recommended Next Courses</h3>
            <div className="space-y-3">
              {[
                { name: 'AP Biology', reason: 'Strengthen science foundation', priority: 'High' },
                { name: 'AP US History', reason: 'Meet social studies requirement', priority: 'Medium' },
                { name: 'Spanish III', reason: 'Complete language requirement', priority: 'High' },
                { name: 'Statistics', reason: 'Additional math credit', priority: 'Low' }
              ].map((course, index) => (
                <div key={index} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                  <div>
                    <h4 className="font-semibold text-gray-900">{course.name}</h4>
                    <p className="text-sm text-gray-600">{course.reason}</p>
                  </div>
                  <Badge
                    variant={course.priority === 'High' ? 'warning' : course.priority === 'Medium' ? 'primary' : 'secondary'}
                    size="sm"
                  >
                    {course.priority}
                  </Badge>
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};